import { z } from 'zod';
import { actionTransition, codeScenario } from './enums.js';
import { echeance } from './entites.js';

// API contracts for the Supabase Edge Functions (services-api). Every error follows the
// envelope { code, message, details?, request_id }.

export const codeErreur = z.enum([
  'SAISIE_INVALIDE',
  'NON_AUTHENTIFIE',
  'ACCES_REFUSE',
  'INTROUVABLE',
  'CONFLIT',
  'REGLE_VIOLEE',
  'ERREUR_INTERNE',
]);
export type CodeErreur = z.infer<typeof codeErreur>;

export const enveloppeErreur = z.object({
  code: codeErreur,
  message: z.string(),
  details: z.unknown().optional(),
  request_id: z.string(),
});
export type EnveloppeErreur = z.infer<typeof enveloppeErreur>;

/** HTTP status mapped to each error code (services-api reference table). */
export const STATUT_HTTP: Readonly<Record<CodeErreur, number>> = {
  SAISIE_INVALIDE: 400,
  NON_AUTHENTIFIE: 401,
  ACCES_REFUSE: 403,
  INTROUVABLE: 404,
  CONFLIT: 409,
  REGLE_VIOLEE: 422,
  ERREUR_INTERNE: 500,
};

// simuler
export const simulerInput = z.object({ simulation_id: z.string().uuid() });
export type SimulerInput = z.infer<typeof simulerInput>;

const pointAnnuel = z.object({ annee: z.number().int(), capital: z.number(), cumul: z.number() });

const kpisScenario = z.object({
  capital_estime: z.number(),
  total_verse: z.number(),
  tri_annualise: z.number().nullable(),
  plus_value: z.number(),
  plus_value_pct: z.number().nullable(),
  // Published variation chips of the reference screen; absent for computed simulations.
  variation_capital_pct: z.number().nullable().optional(),
  variation_rendement_pt: z.number().nullable().optional(),
});

export const simulerOutput = z.object({
  series: z.record(codeScenario, z.array(pointAnnuel)),
  kpis: z.record(codeScenario, kpisScenario),
  hypotheses_version: z.number().int(),
});
export type SimulerOutput = z.infer<typeof simulerOutput>;

// echeance-transition
export const echeanceTransitionInput = z.object({
  echeance_id: z.string().uuid(),
  action: actionTransition,
  nouvelle_date: z.string().date().optional(),
});
export type EcheanceTransitionInput = z.infer<typeof echeanceTransitionInput>;

export const echeanceTransitionOutput = z.object({ echeance });
export type EcheanceTransitionOutput = z.infer<typeof echeanceTransitionOutput>;

// export-pdf
export const exportPdfInput = z.object({ simulation_id: z.string().uuid() });
export type ExportPdfInput = z.infer<typeof exportPdfInput>;

export const exportPdfOutput = z.object({
  document_id: z.string().uuid(),
  url_signee: z.string().url(),
  expire_le: z.string().datetime({ offset: true }),
});
export type ExportPdfOutput = z.infer<typeof exportPdfOutput>;

// generer-occurrences
export const genererOccurrencesOutput = z.object({
  occurrences_creees: z.number().int().nonnegative(),
});
export type GenererOccurrencesOutput = z.infer<typeof genererOccurrencesOutput>;

/** Valid transitions of the échéance state machine (RG-11). */
export const TRANSITIONS_VALIDES: Readonly<Record<string, readonly string[]>> = {
  planifie: ['en_cours', 'annule'],
  // RG-11: from en_cours only validation or rejection; cancellation is allowed on
  // planifie only. Valide and annule are terminal.
  en_cours: ['valide', 'rejete'],
  rejete: ['planifie'],
  valide: [],
  annule: [],
};
